import React, { useEffect, useState } from "react";
import {
  FiAlertTriangle,
  FiChevronRight,
  FiEdit3,
  FiMail,
  FiMapPin,
  FiPhone,
  FiSearch,
  FiUser,
  FiUsers,
} from "react-icons/fi";
import InfoTab from "../tabs/mobile/InfoTab";


const SlideIn = ({ children }: { children: React.ReactNode }) => {
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => setEntered(true));
    return () => window.cancelAnimationFrame(frame);
  }, []);

  return (
    <div
      style={{
        opacity: entered ? 1 : 0,
        transform: entered ? "translateX(0)" : "translateX(18px)",
        transition: "opacity 220ms ease-out, transform 260ms cubic-bezier(0.22, 1, 0.36, 1)",
        willChange: "opacity, transform",
      }}
    >
      {children}
    </div>
  );
};

const firstValue = (...values: any[]) =>
  values.find((value) => value !== undefined && value !== null && String(value).trim() !== "") || "";

const fullName = (person: any) =>
  [person?.firstName, person?.lastName].filter(Boolean).join(" ").trim();

const schoolNameFrom = (parentData: any) => {
  const school = parentData?.schoolSettings || parentData?.school || parentData?.schoolInfo || {};
  return firstValue(school.schoolName, school.name, school.organizationName, parentData?.schoolName, "School");
};

const schoolAddressFrom = (parentData: any) => {
  const school = parentData?.schoolSettings || parentData?.school || parentData?.schoolInfo || {};
  const street = firstValue(school.address, school.street, school.streetAddress);
  const postal = firstValue(school.zipCode, school.postalCode, school.postcode);
  const city = firstValue(school.city, school.town);
  return [street, [postal, city].filter(Boolean).join(" ")].filter(Boolean).join(", ");
};

const Card = ({
  title,
  eyebrow,
  children,
  action,
  onClick,
}: {
  title: string;
  eyebrow?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
  onClick?: () => void;
}) => (
  <section
    onClick={onClick}
    onKeyDown={onClick ? (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onClick();
      }
    } : undefined}
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
    className={`group rounded-[1.75rem] border border-slate-200/80 bg-white/95 p-5 shadow-sm transition duration-300 hover:-translate-y-0.5 hover:border-emerald-200 hover:shadow-[0_18px_40px_-30px_rgba(15,23,42,0.45)] dark:border-slate-800 dark:bg-slate-900 ${onClick ? "cursor-pointer focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-emerald-100 dark:focus-visible:ring-emerald-900/30" : ""}`}
  >
    <div className="mb-4 flex items-start justify-between gap-4">
      <div>
        {eyebrow && (
          <p className="mb-1 text-xs font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
            {eyebrow}
          </p>
        )}
        <h2 className="text-lg font-bold text-slate-950 dark:text-white">{title}</h2>
      </div>
      {action}
    </div>
    {children}
  </section>
);

const TextButton = ({ children, onClick }: { children: React.ReactNode; onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50/80 px-4 py-2 text-sm font-bold text-emerald-800 shadow-sm transition hover:border-emerald-300 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200"
  >
    {children}
    <FiChevronRight size={16} aria-hidden={true} />
  </button>
);

const InfoLine = ({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) => (
  <p className="flex items-start gap-3 text-sm font-medium text-slate-600 dark:text-slate-300">
    <span className="mt-0.5 text-emerald-700 dark:text-emerald-300">{icon}</span>
    <span>{children}</span>
  </p>
);

export default function ParentPortalDashboard({
  t,
  parentData,
  family,
  visibleChildren,
  nextCalendarEvent,
  formatCalendarDate,
  formatCalendarTime,
  totalActions,
  actionSubtexts,
  onOpenCalendar,
  onOpenActions,
  onOpenFamily,
  onOpenDirectory,
  editMode,
  setEditMode,
  form,
  handleChange,
  handleSave,
  newsletterStatus,
  newsletterFirstSubscribed,
  isNewsletterEligible,
  ...rest
}: any) {
  const parentName = fullName(parentData) || t("parentPortal.dashboard.parent", { defaultValue: "Parent" });
  const familyName = family?.familyName || parentData?.lastName || t("parentPortal.dashboard.family", { defaultValue: "Family" });
  const childCount = Array.isArray(visibleChildren) ? visibleChildren.length : 0;
  const schoolName = schoolNameFrom(parentData);
  const schoolAddress = schoolAddressFrom(parentData);
  const hasActions = Number(totalActions || 0) > 0;
  const schoolPhone = firstValue(parentData?.schoolSettings?.phone, parentData?.school?.phone);
  const schoolEmail = firstValue(parentData?.schoolSettings?.email, parentData?.school?.email);
  const [directorySearch, setDirectorySearch] = useState("");

  const openDirectorySearch = () => {
    const query = directorySearch.trim();
    if (typeof window !== "undefined" && query) {
      window.sessionStorage.setItem("parentPortal.directory.initialSearch", query);
    }
    onOpenDirectory?.(query ? { search: query } : undefined);
  };

  const handleDirectorySearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    openDirectorySearch();
  };

  if (editMode) {
    return (
      <InfoTab
        {...rest}
        t={t}
        parentData={parentData}
        editMode={editMode}
        setEditMode={setEditMode}
        form={form}
        handleChange={handleChange}
        handleSave={handleSave}
        newsletterStatus={newsletterStatus}
        newsletterFirstSubscribed={newsletterFirstSubscribed}
        isNewsletterEligible={isNewsletterEligible}
      />
    );
  }

  return (
    <SlideIn>
      <div className="parent-portal-dashboard grid gap-5">
      <header className="flex items-center gap-4 rounded-3xl bg-gradient-to-br from-emerald-50 to-white p-5 ring-1 ring-emerald-100 dark:from-emerald-950/20 dark:to-slate-950/20 dark:ring-emerald-900/50">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-600 text-white shadow-sm" aria-hidden={true}>
          <FiUser size={22} />
        </span>
        <span className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-300">
            {t("parentPortal.dashboard.overview", { defaultValue: "Overview" })}
          </p>
          <h1 className="mt-1 truncate text-2xl font-black tracking-tight text-slate-950 dark:text-slate-50">
            {t("parentPortal.dashboard.welcome", {
              defaultValue: "Welcome, {{name}}",
              name: parentName,
            })}
          </h1>
          <p className="mt-1 text-sm font-medium text-slate-500 dark:text-slate-400">
            {t("parentPortal.dashboard.subtitle", {
              defaultValue: "A quick view of your family, school updates, open actions and contact options.",
            })}
          </p>
        </span>
      </header>

      <div className="grid gap-5 xl:grid-cols-2">
        <Card
          title={t("parentPortal.dashboard.next_activity", { defaultValue: "Next activity" })}
          eyebrow={t("parentPortal.tabs.calendar", { defaultValue: "Calendar" })}
          onClick={onOpenCalendar}
        >
          {nextCalendarEvent ? (
            <div>
              <p className="text-xl font-bold text-slate-950 dark:text-white">{nextCalendarEvent.title}</p>
              <p className="mt-2 text-sm font-semibold text-slate-600 dark:text-slate-300">
                {[formatCalendarDate(nextCalendarEvent.startDate), formatCalendarTime(nextCalendarEvent)]
                  .filter(Boolean)
                  .join(" • ")}
              </p>
            </div>
          ) : (
            <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">
              {t("parentPortal.dashboard.no_next_activity", { defaultValue: "No upcoming calendar items." })}
            </p>
          )}
        </Card>

        <Card
          title={t("parentPortal.dashboard.open_actions", { defaultValue: "Open actions" })}
          eyebrow={hasActions ? t("parentPortal.dashboard.needs_attention", { defaultValue: "Needs attention" }) : t("parentPortal.dashboard.all_set", { defaultValue: "All set" })}
          onClick={onOpenActions}
        >
          <div className="flex items-center gap-3">
            <span className={`rounded-2xl p-3 ring-1 ring-inset ${hasActions ? "bg-amber-50 text-amber-700 ring-amber-200" : "bg-emerald-50 text-emerald-700 ring-emerald-200"}`}>
              <FiAlertTriangle size={20} aria-hidden={true} />
            </span>
            <div>
              <p className="text-2xl font-black text-slate-950 dark:text-white">
                {hasActions ? String(totalActions) : t("parentPortal.dashboard.no_open_actions", { defaultValue: "No open actions" })}
              </p>
              <p className="mt-1 text-sm font-semibold text-slate-600 dark:text-slate-300">
                {hasActions
                  ? actionSubtexts.join(" • ")
                  : t("parentPortal.dashboard.no_open_actions_hint", { defaultValue: "There is nothing you need to complete right now." })}
              </p>
            </div>
          </div>
        </Card>

        <Card
          title={t("parentPortal.dashboard.your_family", { defaultValue: "Your family" })}
          eyebrow={familyName}
          onClick={onOpenFamily}
        >
          <div className="flex items-center gap-3">
            <span className="rounded-2xl bg-emerald-50 p-3 text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-950 dark:text-emerald-200">
              <FiUsers size={20} aria-hidden={true} />
            </span>
            <p className="text-sm font-semibold text-slate-600 dark:text-slate-300">
              {t("parentPortal.dashboard.children_count", {
                defaultValue: "{{count}} child profile(s)",
                count: childCount,
              })}
            </p>
          </div>
        </Card>

        <Card
          title={t("parentPortal.dashboard.school_information", { defaultValue: "School information" })}
          eyebrow={schoolName}
        >
          <div className="grid gap-2">
            {schoolAddress && <InfoLine icon={<FiMapPin size={16} />}>{schoolAddress}</InfoLine>}
            {schoolPhone && <InfoLine icon={<FiPhone size={16} />}>{schoolPhone}</InfoLine>}
            {schoolEmail && <InfoLine icon={<FiMail size={16} />}>{schoolEmail}</InfoLine>}
            {!schoolAddress && !schoolPhone && !schoolEmail && (
              <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">
                {t("parentPortal.school.not_configured", { defaultValue: "School information not configured." })}
              </p>
            )}
          </div>
        </Card>

        <Card
          title={t("parentPortal.dashboard.contact", { defaultValue: "Contact" })}
          eyebrow={t("parentPortal.tabs.directory", { defaultValue: "Directory" })}
        >
          <form className="space-y-3" onSubmit={handleDirectorySearchSubmit}>
            <label className="block text-sm font-semibold text-slate-600 dark:text-slate-300">
              {t("parentPortal.dashboard.directory_search_label", { defaultValue: "Who are you looking for?" })}
            </label>
            <div className="group flex min-h-[48px] items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 shadow-sm transition hover:border-emerald-200 focus-within:border-emerald-400 focus-within:ring-4 focus-within:ring-emerald-100 dark:border-slate-800 dark:bg-slate-950/50 dark:focus-within:ring-emerald-900/30">
              <span className="shrink-0 text-emerald-700 dark:text-emerald-300" aria-hidden={true}><FiSearch size={18} /></span>
              <input
                type="search"
                value={directorySearch}
                onChange={(event) => setDirectorySearch(event.target.value)}
                placeholder={t("parentPortal.dashboard.directory_search_placeholder", {
                  defaultValue: "Search parent, child or address...",
                })}
                className="min-w-0 flex-1 bg-transparent text-sm font-semibold text-slate-900 outline-none placeholder:text-slate-400 dark:text-slate-100"
              />
              <button
                type="submit"
                className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white transition hover:bg-emerald-500"
              >
                {t("common.search", { defaultValue: "Search" })}
                <FiChevronRight size={14} aria-hidden={true} />
              </button>
            </div>
            <button
              type="button"
              onClick={() => onOpenDirectory?.()}
              className="inline-flex items-center gap-1 text-sm font-bold text-emerald-800 transition hover:text-emerald-600 dark:text-emerald-200 dark:hover:text-emerald-100"
            >
              {t("parentPortal.dashboard.open_full_directory", { defaultValue: "Open full address book" })}
              <FiChevronRight size={15} aria-hidden={true} />
            </button>
          </form>
        </Card>

        <Card
          title={t("parentPortal.dashboard.your_details", { defaultValue: "Your details" })}
          action={
            <button
              type="button"
              onClick={() => setEditMode(true)}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-bold shadow-sm text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <FiEdit3 size={16} aria-hidden={true} />
              {t("common.actions.edit", { defaultValue: "Edit" })}
            </button>
          }
        >
          <div className="grid gap-2">
            <InfoLine icon={<FiUser size={16} />}>{parentName}</InfoLine>
            <InfoLine icon={<FiPhone size={16} />}>
              {parentData?.phone || t("parentPortal.dashboard.no_phone", { defaultValue: "No phone number known" })}
            </InfoLine>
            <InfoLine icon={<FiMail size={16} />}>
              {parentData?.email || t("parentPortal.dashboard.no_email", { defaultValue: "No email known" })}
            </InfoLine>
            {parentData?.address && (
              <InfoLine icon={<FiMapPin size={16} />}>{parentData.address}</InfoLine>
            )}
          </div>
        </Card>
      </div>
      </div>
      <style>{`
        .parent-portal-dashboard section {
          transform: translateZ(0);
        }
        @media (prefers-reduced-motion: reduce) {
          .parent-portal-dashboard section {
            transition: none !important;
          }
        }
      `}</style>
    </SlideIn>
  );
}
